import React, { useState, createContext } from 'react'
import { Formik, Field } from 'formik'
import { useQuery } from '@apollo/client'
import ReCAPTCHA from 'react-google-recaptcha'
import { useCreateReview } from 'lib/core/hooks/useCreateReview'
import { Form, Grid, Spinner } from 'lib/ui/components'
import { captchaConfig } from 'services/captchaData'
import { RatingStars } from 'components/Reviews/components/RatingStars'
import { Modal } from 'components/Modal'
import { Button } from 'lib/ui/components/Button'
import { schema, validation } from './schema'
import { Actions, ModalContent } from './components/Styled'
import { useProductByPath } from 'lib/modules/catalog'

export const ReviewsModal = () => {
  const reviews = useReviewsContext()
  const { product } = useProductByPath()
  const productId = product?.id
  const [captcha, setCaptcha] = useState<string | undefined>()
  const { createReview, isLoading } = useCreateReview({
    onSuccess: () => reviews.closeModal(),
    onSuccessShowToast: true,
    onErrorShowToast: true
  })
  const { loading: fetchingCaptchaKey, data: captchaData } = useQuery(captchaConfig)
  const captchaPublicKey = captchaData?.captchaConfig?.public_key

  const handleSubmit = (values: any) => {
    const dataToSubmit = { ...values }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    createReview({
      variables: {
        productId: productId,
        nickname: dataToSubmit?.name,
        title: dataToSubmit?.title,
        detail: dataToSubmit?.reviewContent,
        ratingVotes: [{ rating_id: 1, value: dataToSubmit?.ratingSelected }],
        captcha
      }
    })
  }

  const ReviewCharacterLimit = 10000
  const [textAreaCount, setTextAreaCount] = useState(ReviewCharacterLimit)
  const wordCount = (e: any) => {
    const currentText = e.value
    const characterCount = currentText.length
    const characterLimit = ReviewCharacterLimit
    const TextAreaCharacterRemain = characterLimit - characterCount
    setTextAreaCount(TextAreaCharacterRemain)
  }

  return reviews.isModalOpen ? (
    <Formik
      initialValues={schema}
      validationSchema={validation()}
      onSubmit={handleSubmit}
    >
      {(formikProps: any) => {
        const { setFieldValue, values } = formikProps

        return (
          <Modal
            isOpen
            modalTitle='Product Review'
            type='reviews'
            handleCloseModal={reviews.closeModal}
          >
            <ModalContent>
              {/* <p>Complete the following form to review {productName}.</p> */}
              <Form.Wrapper>
                <Grid>
                  <Field
                    name='title'
                    label='Title'
                    component={Form.Input}
                    className='field'
                  />
                  <div className='ratings'>
                    <label><span>Rating:</span></label>
                    <RatingStars
                      isInteractive
                      onRate={(rate: any) => setFieldValue('ratingSelected', rate.rating)}
                      ratingCount={values.ratingSelected}
                    />
                    <input type='hidden' name='ratingSelected' />
                  </div>
                </Grid>
                <Field
                  name='name'
                  label='Name to Display:'
                  component={Form.Input}
                  className='field'
                />
                <div>
                  <label htmlFor='reviewContent'>
                    <span>Your Review</span>
                    <span className='label-note'>({textAreaCount}/10000 characters remaining)</span>
                  </label>
                  <Field
                    name='reviewContent'
                    component={Form.TextArea}
                    interceptChange={wordCount}
                    className='field'
                  />
                </div>
                <div className='captcha'>
                  {!fetchingCaptchaKey && captchaPublicKey ? (
                    <ReCAPTCHA
                      sitekey={captchaPublicKey}
                      onChange={key => setCaptcha(key ?? undefined)}
                    />
                  ) : (
                    <Spinner />
                  )}
                </div>
                <Actions>
                  <Button
                    type='submit'
                    isLoading={isLoading}
                  >
                    Send
                  </Button>
                </Actions>
              </Form.Wrapper>
            </ModalContent>
          </Modal>
        )
      }}
    </Formik>
  ) : null
}

interface ReviewsContextInterface {
  reviewsCount: number
  setReviewsCount: (count: number | undefined) => any
  setRatingAverage: (rating: number | undefined) => any
  ratingAverage: number
  openModal: () => void
  closeModal: () => void
  isModalOpen: boolean
}

export const ReviewsContext = createContext<ReviewsContextInterface>({
  openModal: () => undefined,
  closeModal: () => undefined,
  isModalOpen: false,
  reviewsCount: 0,
  setReviewsCount: () => undefined,
  setRatingAverage: () => undefined,
  ratingAverage: 0
})

export const ReviewsProvider: React.FC = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [reviewsCount, setReviewsCount] = useState(0)
  const [ratingAverage, setRatingAverage] = useState(0)

  return (
    <ReviewsContext.Provider
      value={{
        reviewsCount: reviewsCount,
        setReviewsCount: setReviewsCount as (count?: number) => any,
        ratingAverage: ratingAverage,
        setRatingAverage: setRatingAverage as (rating?: number) => any,
        openModal: () => setIsOpen(true),
        closeModal: () => setIsOpen(false),
        isModalOpen: isOpen
      }}
    >
      {children}
    </ReviewsContext.Provider>
  )
}

export const useReviewsContext = () => {
  const context = React.useContext(ReviewsContext)
  if (context === undefined) {
    throw new Error('useReviewsContext must be used within a ReviewsProvider')
  }
  return context
}
